import React, { useState } from 'react';
import { StyleSheet, Pressable, View } from 'react-native';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import ChatInputBanner from '@/components/ChatInput/ChatInputBanner';
import useTranslation from '@/hooks/useTranslation';

const createStyles = (colors) => StyleSheet.create({
  dropdownContent: {
    padding: 10,
    minWidth: 340,
    maxWidth: 340,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 15,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: colors.border_color_secondary,
  },
  iconContainer: {
    width: 45,
    height: 45,
    borderRadius: 25,
    backgroundColor: colors.color_destructive + '15',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTextContainer: {
    flex: 1,
  },
  infoSection: {
    marginBottom: 15,
  },
  limitButton: {
    textDecorationLine: 'underline',
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 4,
  },
  infoLabel: {
    flex: 1,
  }
});

function ChatInputMessageContextBanner({
  level,
  percentageOver = 1,
  hasAttachment = false,
  isCloseable = false,
  effectiveMaxInputTokens,
  currentInputTokens,
  isDetached = false,
}) {
  const { colors } = useTheme();
  const styles = createStyles(colors);
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const baseMessage = t('chatInput.messageOverLimitPre').replace('%PERCENT%', percentageOver);

  const actionMessage = hasAttachment
    ? t('chatInput.messageOverLimitAttachment')
    : t('chatInput.messageOverLimitText');
  
  return (
    <>
      <ChatInputBanner color="danger" level={level} isCloseable={isCloseable} isDetached={isDetached}>
        {baseMessage}
        <DropdownMenu.Root open={isOpen} onOpenChange={setIsOpen}>
          <DropdownMenu.Trigger>
            <Pressable style={styles.limitButton}>
              <Text color="primary" weight="medium" size="s">{t('chatInput.lengthLimit')}</Text>
            </Pressable>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content className="DropdownMenuContent">
            <View style={styles.dropdownContent}>
              <View style={styles.header}>
                <View style={styles.iconContainer}>
                  <Icon 
                    name="exclamation-triangle" 
                    size={20}
                    color={colors.color_destructive}
                  />
                </View>
                <View style={styles.headerTextContainer}>
                  <Text size="l" weight="bold" color="primary">{t('chatInput.contextLength')}</Text>
                  <Text size="s" color="secondary">{t('chatInput.contextLengthSubtitle')}</Text>
                </View>
              </View>

              <View style={styles.infoSection}>
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel} color="secondary" size="s">{t('chatInput.currentMessageLength')}</Text>
                  <Text color="primary" weight="medium">{currentInputTokens} {t('chatInput.tokens')}</Text>
                </View>
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel} color="secondary" size="s">{t('chatInput.modelInputLimit')}</Text>
                  <Text color="primary" weight="medium">{effectiveMaxInputTokens} {t('chatInput.tokens')}</Text>
                </View>
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel} color="secondary" size="s">{t('chatInput.overLimit')}</Text>
                  <Text color="danger" weight="medium">{currentInputTokens - effectiveMaxInputTokens} {t('chatInput.tokens')} ({percentageOver}%)</Text>
                </View>
              </View>

              <Text size="xs" color="secondary">
                {t('chatInput.contextLengthDescription').replace('%MAX%', effectiveMaxInputTokens)}
              </Text>
            </View>
          </DropdownMenu.Content>
        </DropdownMenu.Root>
        {actionMessage}
      </ChatInputBanner>
    </>
  );
}

ChatInputMessageContextBanner.propTypes = {
  style: PropTypes.object,
  percentageOver: PropTypes.number.isRequired,
  hasAttachment: PropTypes.bool
}; 

export default React.memo(ChatInputMessageContextBanner);